import React, { Fragment } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { HiChevronDown } from "react-icons/hi";
import { Link } from "react-router-dom";
import useHeaderDailyTargets from "../../hooks/useHeaderDailyTargets";
import { getAdminPanelRole } from "../../utils/adminPanelRole";

const ROLES_WITH_DAILY_TARGETS = new Set([
  "admin",
  "developer",
  "csr",
  "csr team lead",
  "chat support",
]);

export function canSeeDailyTargetsHeader() {
  if (typeof window === "undefined") return false;
  const role = getAdminPanelRole();
  return ROLES_WITH_DAILY_TARGETS.has(role);
}

function formatDateLabel(dateKey) {
  if (!dateKey) return "Today";
  const d = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function pct(actual, target) {
  const t = Number(target) || 0;
  if (t <= 0) return null;
  return Math.min(100, Math.round(((Number(actual) || 0) / t) * 100));
}

function BigProgress({ label, actual, target, accent = "#FB565F" }) {
  const p = pct(actual, target);
  const has = Number(target) > 0;
  const done = Number(actual) || 0;
  const goal = Number(target) || 0;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between gap-2 text-sm">
        <span className="font-semibold text-gray-700">{label}</span>
        <span className="tabular-nums font-black text-gray-900">
          {has ? (
            <>
              {done} <span className="text-gray-400 font-semibold">/</span> {goal}
              {p != null ? (
                <span className="ml-1.5 text-xs font-bold text-gray-500">({p}%)</span>
              ) : null}
            </>
          ) : (
            <span className="text-xs font-normal text-gray-400">Not set</span>
          )}
        </span>
      </div>
      <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${has ? p || 0 : 0}%`, backgroundColor: accent }}
        />
      </div>
    </div>
  );
}

function StatusPill({ targetsSet, completed }) {
  if (!targetsSet) {
    return (
      <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
        No target
      </span>
    );
  }
  if (completed) {
    return (
      <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">
        Done
      </span>
    );
  }
  return (
    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900">
      In progress
    </span>
  );
}

function csrDone(mine) {
  if (!mine?.targets?.targetsSet) return false;
  const callsOk =
    (Number(mine.actuals?.callsCount) || 0) >= (Number(mine.targets.callsTarget) || 0);
  if (mine.targets.targetMode === "calls_only") return callsOk;
  return (
    callsOk &&
    (Number(mine.actuals?.premiumCount) || 0) >= (Number(mine.targets.premiumTarget) || 0)
  );
}

function chatDone(mine) {
  if (!mine?.targets?.targetsSet) return false;
  const t = mine.targets;
  const a = mine.actuals || {};
  const checks = [
    [a.chatMessages, t.chatMessages],
    [a.emailsAdded, t.emailsAdded],
    [a.socialResponses, t.socialResponses],
    [a.randomRequests, t.randomRequests],
  ];
  return checks.every(([done, goal]) => {
    const g = Number(goal) || 0;
    if (g <= 0) return true;
    return (Number(done) || 0) >= g;
  });
}

function TeamRow({ name, children, status }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-3.5 py-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-bold text-gray-900 truncate">{name}</p>
        {status}
      </div>
      {children}
    </div>
  );
}

function ChatSupportSection({ section }) {
  if (!section) return null;
  const { canViewTeam, mine, team } = section;
  const teamOthers = (team || []).filter((row) => !row.isTeamLeadSelf);

  return (
    <div className="space-y-3">
      <p className="text-xs font-black uppercase tracking-wider text-cyan-700">
        Chat Support
      </p>
      {mine ? (
        <div className="rounded-2xl border-2 border-cyan-100 bg-gradient-to-br from-cyan-50/80 to-white p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-base font-black text-gray-900">Your targets</p>
              {mine.agentName ? (
                <p className="text-xs text-gray-500">{mine.agentName}</p>
              ) : null}
            </div>
            <StatusPill
              targetsSet={mine.targets?.targetsSet}
              completed={chatDone(mine)}
            />
          </div>
          {mine.targets?.targetsSet ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <BigProgress
                label="Chats"
                actual={mine.actuals?.chatMessages}
                target={mine.targets.chatMessages}
                accent="#0891b2"
              />
              <BigProgress
                label="Emails"
                actual={mine.actuals?.emailsAdded}
                target={mine.targets.emailsAdded}
                accent="#0891b2"
              />
              <BigProgress
                label="Social"
                actual={mine.actuals?.socialResponses}
                target={mine.targets.socialResponses}
                accent="#0891b2"
              />
              <BigProgress
                label="Random"
                actual={mine.actuals?.randomRequests}
                target={mine.targets.randomRequests}
                accent="#0891b2"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500">No Chat Support target set for today.</p>
          )}
        </div>
      ) : null}
      {canViewTeam && teamOthers.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
            Team
          </p>
          {teamOthers.map((row) => (
            <TeamRow
              key={row.agentId}
              name={row.agentName || `Agent #${row.agentId}`}
              status={
                <StatusPill
                  targetsSet={row.targets?.targetsSet}
                  completed={chatDone(row)}
                />
              }
            >
              {row.targets?.targetsSet ? (
                <p className="text-xs text-gray-600 tabular-nums">
                  Chat {Number(row.actuals?.chatMessages) || 0}/
                  {row.targets.chatMessages} · Email{" "}
                  {Number(row.actuals?.emailsAdded) || 0}/{row.targets.emailsAdded}
                </p>
              ) : (
                <p className="text-xs text-gray-400">No target</p>
              )}
            </TeamRow>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CsrOutreachSection({ section }) {
  if (!section) return null;
  const { canViewTeam, mine, team } = section;
  const teamOthers = (team || []).filter((row) => !row.isTeamLeadSelf);

  return (
    <div className="space-y-3">
      <p className="text-xs font-black uppercase tracking-wider text-teal-700">CSR</p>
      {mine ? (
        <div className="rounded-2xl border-2 border-teal-100 bg-gradient-to-br from-teal-50/80 to-white p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-base font-black text-gray-900">Your targets</p>
              {mine.agentName ? (
                <p className="text-xs text-gray-500">{mine.agentName}</p>
              ) : null}
            </div>
            <StatusPill
              targetsSet={mine.targets?.targetsSet}
              completed={csrDone(mine)}
            />
          </div>
          {mine.targets?.targetsSet ? (
            <div className="space-y-3">
              <BigProgress
                label="Calls / messages"
                actual={mine.actuals?.callsCount}
                target={mine.targets.callsTarget}
                accent="#0d9488"
              />
              {mine.targets.targetMode !== "calls_only" ? (
                <BigProgress
                  label="Premium"
                  actual={mine.actuals?.premiumCount}
                  target={mine.targets.premiumTarget}
                  accent="#0d9488"
                />
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No CSR target set for today.</p>
          )}
        </div>
      ) : null}
      {canViewTeam && teamOthers.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
            Team
          </p>
          {teamOthers.map((row) => (
            <TeamRow
              key={row.agentId}
              name={row.agentName || `Agent #${row.agentId}`}
              status={
                <StatusPill
                  targetsSet={row.targets?.targetsSet}
                  completed={csrDone(row)}
                />
              }
            >
              {row.targets?.targetsSet ? (
                <p className="text-xs text-gray-600 tabular-nums">
                  Calls {Number(row.actuals?.callsCount) || 0}/{row.targets.callsTarget}
                  {row.targets.targetMode !== "calls_only"
                    ? ` · Premium ${Number(row.actuals?.premiumCount) || 0}/${row.targets.premiumTarget}`
                    : ""}
                </p>
              ) : (
                <p className="text-xs text-gray-400">No target</p>
              )}
            </TeamRow>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function buildHeaderSummary(data, loading, error) {
  const csrMine = data?.csr?.mine;
  const chatMine = data?.chatSupport?.mine;

  if (csrMine) {
    const done = Number(csrMine.actuals?.callsCount) || 0;
    const target = Number(csrMine.targets?.callsTarget) || 0;
    if (csrMine.targets?.targetsSet && target > 0) {
      return { label: `${done}/${target} calls`, met: done >= target };
    }
    if (done > 0) {
      return { label: `${done} calls today`, met: false };
    }
  }

  if (chatMine?.targets?.targetsSet) {
    const t = chatMine.targets;
    const done = Number(chatMine.actuals?.chatMessages) || 0;
    const target = Number(t.chatMessages) || 0;
    if (target > 0) {
      return { label: `${done}/${target} chats`, met: done >= target };
    }
  }

  if (loading && !data) return { label: "Loading…", met: false };
  if (error) return { label: "Tap to retry", met: false };
  return { label: "Daily targets", met: false };
}

export default function DailyTargetsDropdown() {
  const enabled = canSeeDailyTargetsHeader();
  const { data, loading, error, refresh } = useHeaderDailyTargets(enabled);

  if (!enabled) return null;

  const summary = buildHeaderSummary(data, loading, error);
  const hasChat = Boolean(data?.chatSupport);
  const hasCsr = Boolean(data?.csr);

  return (
    <Popover className="relative">
      <PopoverButton
        type="button"
        className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2 text-sm font-semibold shadow-sm outline-none transition-colors ${
          summary.met
            ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
            : "border-rose-200 bg-rose-50 text-gray-800 hover:bg-rose-100"
        }`}
        title="Today's daily targets"
      >
        <span className="text-[10px] uppercase tracking-wide font-black text-[#FF2D38]">
          Target
        </span>
        <span className={`font-black ${summary.met ? "text-green-700" : "text-gray-900"}`}>
          {summary.label}
        </span>
        <HiChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <PopoverPanel className="absolute right-0 z-50 mt-2 w-[min(98vw,920px)] max-h-[min(85vh,720px)] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4 mb-4">
            <div>
              <p className="text-xl sm:text-2xl font-black text-gray-900">Daily targets</p>
              <p className="text-sm text-gray-500 mt-1">
                CSR and Chat Support side by side — progress for you and your team.
              </p>
            </div>
            <span className="text-xs font-bold text-gray-500 shrink-0 rounded-full bg-gray-100 px-3 py-1.5">
              {formatDateLabel(data?.targetDate)}
            </span>
          </div>

          {loading && !data ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading targets…</p>
          ) : null}

          <div
            className={`grid gap-5 ${
              hasChat && hasCsr ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
            }`}
          >
            {hasChat ? <ChatSupportSection section={data.chatSupport} /> : null}
            {hasCsr ? <CsrOutreachSection section={data.csr} /> : null}
          </div>

          {!hasChat && !hasCsr && !loading ? (
            <div className="py-6 text-center">
              <p className="text-sm text-gray-500">
                {error ? "Could not load targets." : "No targets set for today yet."}
              </p>
              <button
                type="button"
                onClick={() => refresh()}
                className="mt-3 text-sm font-bold text-[#FF2D38] hover:underline"
              >
                Refresh
              </button>
            </div>
          ) : null}

          <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-3 justify-between">
            <button
              type="button"
              onClick={() => refresh()}
              className="text-xs font-semibold text-gray-500 hover:text-gray-800"
            >
              Refresh
            </button>
            <Link
              to="/my-desk?section=targets"
              className="text-xs font-bold text-[#FB565F] hover:underline"
            >
              Open My Desk targets →
            </Link>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
